import { BaseModule } from 'quill-image-resize-module-react/src/modules/BaseModule';
import { ReactComponent as IconAlignLeft } from 'assets/svg/align-left.svg';
import { ReactComponent as IconAlignCenter } from 'assets/svg/align-center.svg';
import { ReactComponent as IconAlignRight } from 'assets/svg/align-right.svg';
import ReactDOMServer from 'react-dom/server';

type ButtonType = 'resize' | 'alignment';

interface SizeItem {
    name?: string;
    icon?: any;
    apply: () => void;
    isApplied: () => boolean;
    type: ButtonType;
}

let Parchment: any = {};
let WidthStyle: any = {};
let FloatStyle: any = {};
let MarginStyle: any = {};
let DisplayStyle: any = {};

const MIN_IMAGE_WIDTH = 20;

export class ImageResizePercentageModule extends BaseModule {
    alignments: SizeItem[] = [];
    buttons: { item: SizeItem, element: HTMLSpanElement }[] = [];
    boxes: HTMLDivElement[] = [];
    dragBox: HTMLDivElement | null = null;
    dragStartX = 0;
    preDragWidth = 0;

    onCreate = (parchment: any) => {
        Parchment = parchment;
        WidthStyle = new Parchment.Attributor.Style('width', 'width');
        FloatStyle = new Parchment.Attributor.Style('float', 'float');
        MarginStyle = new Parchment.Attributor.Style('margin', 'margin');
        DisplayStyle = new Parchment.Attributor.Style('display', 'display');

        // Setup Toolbar
        this.toolbar = document.createElement('div');
        Object.assign(this.toolbar.style, this.options.imageResizeStyles);
        this.overlay.appendChild(this.toolbar);

        // Setup Buttons
        this._defineAlignments();
        this._addToolbarButtons();

        // Setup free-resize drag handles
        this._addResizeHandles();
    };

    onDestroy = () => {
        document.removeEventListener('mousemove', this.handleDrag);
        document.removeEventListener('mouseup', this.handleMouseup);
    };

    onUpdate = () => {
        this._positionBoxes();
    };

    _defineAlignments = () => {
        this.alignments = [
            {
                icon: ReactDOMServer.renderToString(<IconAlignLeft />),
                apply: () => {
                    DisplayStyle.add(this.img, 'block');
                    FloatStyle.remove(this.img);
                    MarginStyle.add(this.img, '0 0 0 0');
                },
                isApplied: () => FloatStyle.value(this.img) === 'left',
                type: 'alignment',
            },
            {
                icon: ReactDOMServer.renderToString(<IconAlignCenter />),
                apply: () => {
                    DisplayStyle.add(this.img, 'block');
                    FloatStyle.remove(this.img);
                    MarginStyle.add(this.img, 'auto');
                },
                isApplied: () => MarginStyle.value(this.img) === 'auto',
                type: 'alignment',
            },
            {
                icon: ReactDOMServer.renderToString(<IconAlignRight />),
                apply: () => {
                    DisplayStyle.add(this.img, 'block');
                    FloatStyle.remove(this.img);
                    MarginStyle.add(this.img, '0 0 0 auto');
                },
                isApplied: () => FloatStyle.value(this.img) === 'right',
                type: 'alignment',
            },
            {
                name: '25%',
                apply: () => {
                    WidthStyle.add(this.img, '25%');
                },
                isApplied: () => WidthStyle.value(this.img) === '25%',
                type: 'resize',
            },
            {
                name: '50%',
                apply: () => {
                    WidthStyle.add(this.img, '50%');
                },
                isApplied: () => WidthStyle.value(this.img) === '50%',
                type: 'resize',
            },
            {
                name: '100%',
                apply: () => {
                    WidthStyle.add(this.img, '100%');
                },
                isApplied: () => WidthStyle.value(this.img) === '100%',
                type: 'resize',
            },
        ];
    };

    _addToolbarButtons = () => {
        this.buttons = [];
        this.alignments.forEach((alignment, idx) => {
            const button = document.createElement('span');
            this.buttons.push({
                item: alignment,
                element: button,
            });
            button.innerHTML = alignment.icon ? alignment.icon : `<span>${alignment.name}</span>`;
            button.addEventListener('click', () => {
                if (alignment.isApplied() && alignment.type === 'resize') {
                    return;
                }

                // deselect all buttons
                this.buttons.filter(x => x.item.type === alignment.type).forEach(b => {
                    b.element.style.filter = '';
                });

                if (alignment.isApplied()) {
                    // If applied, unapply
                    WidthStyle.remove(this.img);
                    FloatStyle.remove(this.img);
                    MarginStyle.remove(this.img);
                    DisplayStyle.remove(this.img);
                } else {
                    // otherwise, select button and apply
                    this._selectButton(button);
                    alignment.apply();
                }
                // image may change position; redraw drag handles
                this.requestUpdate();
            });
            Object.assign(button.style, this.options.imageResizeButtonStyles);
            if (idx > 0) {
                button.style.borderLeftWidth = '0';
            }

            if (button.children[0]) {
                Object.assign((button.children[0] as any).style, this.options.toolbarButtonSvgStyles);
            }

            if (alignment.isApplied()) {
                // select button if previously applied
                this._selectButton(button);
            }
            this.toolbar.appendChild(button);
        });
    };

    _selectButton = (button: HTMLSpanElement) => {
        button.style.filter = 'invert(20%)';
    };

    _addResizeHandles = () => {
        this.boxes = [];
        this._createBox('nwse-resize'); // top-left
        this._createBox('nesw-resize'); // top-right
        this._createBox('nwse-resize'); // bottom-right
        this._createBox('nesw-resize'); // bottom-left
        this._positionBoxes();
    };

    _createBox = (cursor: string) => {
        const box = document.createElement('div');
        Object.assign(box.style, this.options.handleStyles);
        box.style.cursor = cursor;
        box.style.width = `${this.options.handleStyles.width}`;
        box.style.height = `${this.options.handleStyles.height}`;
        box.addEventListener('mousedown', this.handleMousedown);
        this.overlay.appendChild(box);
        this.boxes.push(box);
    };

    _positionBoxes = () => {
        if (this.boxes.length !== 4) return;
        const handleW = parseFloat(this.options.handleStyles.width);
        const handleH = parseFloat(this.options.handleStyles.height);
        const xOff = `${-handleW / 2}px`;
        const yOff = `${-handleH / 2}px`;
        const positions: Array<Partial<CSSStyleDeclaration>> = [
            { left: xOff, top: yOff, right: '', bottom: '' },
            { right: xOff, top: yOff, left: '', bottom: '' },
            { right: xOff, bottom: yOff, left: '', top: '' },
            { left: xOff, bottom: yOff, right: '', top: '' },
        ];
        positions.forEach((pos, idx) => {
            Object.assign(this.boxes[idx].style, pos);
        });
    };

    handleMousedown = (evt: MouseEvent) => {
        this.dragBox = evt.target as HTMLDivElement;
        this.dragStartX = evt.clientX;
        this.preDragWidth = this.img.width || this.img.naturalWidth;
        document.addEventListener('mousemove', this.handleDrag);
        document.addEventListener('mouseup', this.handleMouseup);
    };

    handleDrag = (evt: MouseEvent) => {
        if (!this.img || !this.dragBox) return;
        const deltaX = evt.clientX - this.dragStartX;
        const isLeftHandle = this.dragBox === this.boxes[0] || this.dragBox === this.boxes[3];
        const newWidth = Math.max(MIN_IMAGE_WIDTH, Math.round(isLeftHandle ? this.preDragWidth - deltaX : this.preDragWidth + deltaX));
        WidthStyle.add(this.img, `${newWidth}px`);

        // dragging overrides any preset percentage — deselect resize buttons
        this.buttons.filter(x => x.item.type === 'resize').forEach(b => {
            b.element.style.filter = '';
        });

        this.requestUpdate();
    };

    handleMouseup = () => {
        this.dragBox = null;
        document.removeEventListener('mousemove', this.handleDrag);
        document.removeEventListener('mouseup', this.handleMouseup);
    };
}
